import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import { AdminLoginSchema } from "../../../ValidationSchema";
import axios from "axios";
import { Eye, EyeOff, LogIn } from "lucide-react";

const AdminLogin = () => {
  const location = useLocation();
  const Navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const isAlertShown = useRef(false);

  const togglePassword = () => setShowPassword((prev) => !prev);
  const [keepSignedIn, setKeepSignedIn] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("Token");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (token && user?.user_type === "supplier") {
      Navigate("/admin/dashboard", { replace: true });
    }
  }, [Navigate]);

  useEffect(() => {
    if (location?.state && !isAlertShown.current) {
      toast.success(location.state);
      isAlertShown.current = true;
    }
    window.history.replaceState({}, location?.state);
  }, [location]);

  const login = useFormik({
    initialValues: { identifier: "", password: "", firebase_token: "" },
    validationSchema: AdminLoginSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const isEmail = /\S+@\S+\.\S+/.test(values.identifier);
        const payload = {
          password: values.password,
          ...(isEmail
            ? { email: values.identifier }
            : { phone: values.identifier }),
        };

        const response = await axios.post(
          `${process.env.REACT_APP_URL}/admin/auth/login`,
          payload
        );

        const { accessToken, user } = response.data.data;
        localStorage.setItem("Token", accessToken);
        // localStorage.setItem("RefreshToken", refreshToken);
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("user_type", user.user_type);
        localStorage.setItem("login_time", new Date().toISOString());

        toast.success(`Welcome, ${user.username || user.name}`);
        Navigate("/admin/dashboard", { replace: true });
        resetForm();
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Login failed. Please try again."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const C = {
    primary: "#4F46E5",
    primary2: "#4338CA",
    text: "#0C1424",
    text2: "#5B6B82",
    text3: "#90A0B5",
    border: "#E9EDF3",
    surface: "#FFFFFF",
    surface2: "#F8FAFC",
  };

  const fieldBox = (hasError) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    height: 46,
    padding: "0 14px",
    background: C.surface,
    border: `1px solid ${hasError ? "#e11d48" : C.border}`,
    borderRadius: 11,
  });
  const fieldInput = {
    border: 0,
    outline: 0,
    background: "transparent",
    fontFamily: "inherit",
    fontSize: 14,
    color: C.text,
    width: "100%",
  };
  const labelStyle = {
    display: "block",
    fontSize: 12.5,
    fontWeight: 700,
    color: C.text2,
    marginBottom: 8,
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        fontFamily:
          "'Plus Jakarta Sans', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        letterSpacing: "-0.01em",
        color: C.text,
        WebkitFontSmoothing: "antialiased",
      }}
    >
      <style>{`
        @keyframes lg-floaty { 0%,100%{ transform: translateY(0) rotate(0deg);} 50%{ transform: translateY(-18px) rotate(4deg);} }
        @keyframes lg-floaty2 { 0%,100%{ transform: translateY(0) rotate(0deg);} 50%{ transform: translateY(14px) rotate(-5deg);} }
        @keyframes lg-fadeUp { from{ opacity:0; transform: translateY(14px);} to{ opacity:1; transform: translateY(0);} }
        .lg-input::placeholder { color: ${C.text3}; }
        .lg-submit:hover { filter: brightness(1.06); }
        .lg-forgot:hover { text-decoration: underline; }
        @media (max-width: 900px) { .lg-brand { display: none !important; } }
      `}</style>

      {/* left brand panel */}
      <div
        className="lg-brand"
        style={{
          flex: 1.05,
          position: "relative",
          overflow: "hidden",
          background:
            "linear-gradient(150deg,#312E81 0%,#4338CA 45%,#4F46E5 100%)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "52px 56px",
          color: "#fff",
        }}
      >
        <div style={{ position: "absolute", top: -80, right: -60, width: 280, height: 280, borderRadius: "34% 66% 60% 40%/44% 45% 55% 56%", background: "rgba(255,255,255,.07)", animation: "lg-floaty 9s ease-in-out infinite" }} />
        <div style={{ position: "absolute", bottom: -70, left: -50, width: 230, height: 230, borderRadius: "60% 40% 45% 55%/55% 48% 52% 45%", background: "rgba(255,255,255,.06)", animation: "lg-floaty2 11s ease-in-out infinite" }} />
        <div style={{ position: "absolute", top: "38%", right: "14%", width: 90, height: 90, borderRadius: "28%", background: "rgba(255,255,255,.05)", animation: "lg-floaty 7s ease-in-out infinite" }} />

        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 11, background: "rgba(255,255,255,.16)", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(255,255,255,.22)" }}>
            <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></svg>
          </div>
          <div style={{ lineHeight: 1.05 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>MyPackGenie</div>
            <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.7, letterSpacing: ".08em", textTransform: "uppercase" }}>Admin Console</div>
          </div>
        </div>

        <div style={{ position: "relative", maxWidth: 440 }}>
          <h1 style={{ margin: "0 0 16px", fontSize: 36, fontWeight: 800, lineHeight: 1.15, letterSpacing: "-.03em", color: "#fff" }}>
            Run your packaging marketplace with confidence.
          </h1>
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.65, opacity: 0.82, color: "#fff" }}>
            Manage buyers, suppliers, RFQs and the full production workflow — from quotation to dispatch — in one premium console.
          </p>
          <div style={{ display: "flex", gap: 26, marginTop: 34 }}>
            <div><div style={{ fontSize: 26, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace" }}>25+</div><div style={{ fontSize: 12, opacity: 0.72, fontWeight: 500 }}>Active users</div></div>
            <div><div style={{ fontSize: 26, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace" }}>18</div><div style={{ fontSize: 12, opacity: 0.72, fontWeight: 500 }}>Live orders</div></div>
            <div><div style={{ fontSize: 26, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace" }}>7</div><div style={{ fontSize: 12, opacity: 0.72, fontWeight: 500 }}>Workflow stages</div></div>
          </div>
        </div>

        <div style={{ position: "relative", fontSize: 12, opacity: 0.6 }}>
          © {new Date().getFullYear()} MyPackGenie · All rights reserved
        </div>
      </div>

      {/* right form panel */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40, background: C.surface2 }}>
        <div style={{ width: "100%", maxWidth: 380, animation: "lg-fadeUp .5s ease both" }}>
          <div style={{ marginBottom: 30 }}>
            <h2 style={{ margin: "0 0 7px", fontSize: 26, fontWeight: 800, letterSpacing: "-.025em" }}>Welcome back</h2>
            <p style={{ margin: 0, fontSize: 14, color: C.text2 }}>Sign in to manage your MyPackGenie Admin Dashboard.</p>
          </div>

          <form onSubmit={login.handleSubmit}>
            {/* Email / Phone */}
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Email or Phone</label>
              <div style={fieldBox(login.errors.identifier && login.touched.identifier)}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={C.text3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 5L2 7" /></svg>
                <input
                  className="lg-input"
                  type="text"
                  name="identifier"
                  placeholder="Enter your email or phone"
                  value={login.values.identifier}
                  onChange={login.handleChange}
                  onBlur={login.handleBlur}
                  style={fieldInput}
                />
              </div>
              {login.errors.identifier && login.touched.identifier && (
                <small style={{ color: "#e11d48", fontSize: 12, fontWeight: 600, marginTop: 6, display: "block" }}>{login.errors.identifier}</small>
              )}
            </div>

            {/* Password */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>Password</label>
                <a
                  href="#"
                  className="lg-forgot"
                  onClick={(e) => {
                    e.preventDefault();
                    toast("Please contact your administrator to reset your password.", { icon: "🔑" });
                  }}
                  style={{ fontSize: 12, fontWeight: 700, color: C.primary, textDecoration: "none" }}
                >
                  Forgot password?
                </a>
              </div>
              <div style={fieldBox(login.errors.password && login.touched.password)}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={C.text3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                <input
                  className="lg-input"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  value={login.values.password}
                  onChange={login.handleChange}
                  onBlur={login.handleBlur}
                  style={fieldInput}
                />
                <button type="button" onClick={togglePassword} aria-label="Show password" style={{ border: 0, background: "transparent", color: C.text3, cursor: "pointer", display: "flex", padding: 0 }}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {login.errors.password && login.touched.password && (
                <small style={{ color: "#e11d48", fontSize: 12, fontWeight: 600, marginTop: 6, display: "block" }}>{login.errors.password}</small>
              )}
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 9, margin: "0 0 24px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={keepSignedIn}
                onChange={(e) => setKeepSignedIn(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: C.primary, cursor: "pointer" }}
              />
              <span style={{ fontSize: 13, color: C.text2, fontWeight: 500 }}>Keep me signed in</span>
            </label>

            <button
              type="submit"
              disabled={login.isSubmitting}
              className="lg-submit"
              style={{
                width: "100%",
                height: 47,
                border: 0,
                borderRadius: 11,
                background: `linear-gradient(135deg, ${C.primary}, ${C.primary2})`,
                color: "#fff",
                fontFamily: "inherit",
                fontSize: 14.5,
                fontWeight: 700,
                cursor: login.isSubmitting ? "not-allowed" : "pointer",
                opacity: login.isSubmitting ? 0.8 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 9,
                boxShadow: "0 4px 14px rgba(79,70,229,.35)",
              }}
            >
              <LogIn size={18} />
              {login.isSubmitting ? "Logging in..." : "Login"}
            </button>
          </form>

          <p style={{ margin: "26px 0 0", textAlign: "center", fontSize: 12.5, color: C.text3 }}>
            Secured admin access · MyPackGenie
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
